import {type UserOccupationInfo} from 'src/lib/fetchers/fetch-user-occupation-info';

import {TECH_OCCUPATION_GROUP_IDS} from './occupation-constants';

/**
 * Determines if a user is considered a "tech learner" based on their occupation group.
 */
export const isTechLearner = (userOccupationInfo: UserOccupationInfo): boolean => {
    if (!userOccupationInfo?.group?.id) {
        return false;
    }

    const groupId = Number(userOccupationInfo.group.id);
    return TECH_OCCUPATION_GROUP_IDS.includes(groupId);
};

/**
 * Determines if in-product occupation collection should be enabled based on feature flag and eligibility.
 *
 * Mirrors the monolith (personal-plan-logged-in-home): the OccupationCollectionBanner is only
 * shown to users who have NO occupation context at all (no canonical, no group). Users who already
 * have a group set are considered to have enough occupation context — the lectures section handles
 * surfacing relevant content for them.
 *
 * Note: the monolith computes this server-side (Django). Django's `isApprovedOccupation` and the
 * GraphQL `canonical` field can diverge (Django may consider an occupation approved while GraphQL
 * returns canonical=null). Always treating hasOccupationGroup as a signal to suppress the banner
 * prevents it from incorrectly appearing for users whose occupation Django considers approved.
 */
export const calculateEnableInProductOccupationCollection = (
    isPersonalPlan: boolean,
    userOccupationInfo: UserOccupationInfo,
): boolean => {
    if (!isPersonalPlan) {
        return false;
    }

    const isManager = Boolean(userOccupationInfo?.isManager);
    const hasOccupation = Boolean(userOccupationInfo?.canonical?.id);
    const hasOccupationGroup = Boolean(userOccupationInfo?.group?.id);

    // Show only when the user has no occupation context at all (no canonical and no group).
    // If the user has a group the lectures section already surfaces relevant content for them.
    return !isManager && !hasOccupation && !hasOccupationGroup;
};
